import React, { useId, useMemo, useRef, useState } from "react";
import { compactNum } from "../../utils/bettorsDeskHelpers";
import {
  HOUSE_FLOW_ORDER,
  houseFlowMeta,
  houseKeepRate,
} from "../../utils/ticketsDeskHelpers";

const clamp01 = (v) => Math.min(1, Math.max(0, v));

/* ─── Volume: tickets placed (bars) + staked amount (line) ─── */

const VOL_W = 360;
const VOL_H = 140;
const VOL_PT = 12;
const VOL_PB = 20;

export const TicketVolumeCombo = ({ items, ariaLabel }) => {
  const gradId = useId();
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const days = useMemo(
    () =>
      (Array.isArray(items) ? items : []).map((d) => ({
        date: d.date,
        label: d.label,
        count: Number(d.count) || 0,
        sumStake: Number(d.sumStake) || 0,
      })),
    [items]
  );

  const n = days.length;
  const countMax = Math.max(1, ...days.map((d) => d.count));
  const stakeMax = Math.max(1, ...days.map((d) => d.sumStake));
  const innerH = VOL_H - VOL_PT - VOL_PB;
  const slot = n > 0 ? VOL_W / n : VOL_W;
  const barW = Math.min(slot * 0.46, 26);

  const yCount = (v) => VOL_PT + (1 - v / countMax) * innerH;

  const { linePath, areaPath, points } = useMemo(() => {
    if (!n) return { linePath: "", areaPath: "", points: [] };
    const pts = days.map((d, i) => ({
      x: (i + 0.5) * slot,
      y: VOL_PT + (1 - d.sumStake / stakeMax) * innerH,
      stake: d.sumStake,
    }));
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i += 1) {
      d += ` L ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`;
    }
    const base = VOL_H - VOL_PB;
    return {
      linePath: d,
      areaPath: `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${base} L ${pts[0].x.toFixed(1)} ${base} Z`,
      points: pts,
    };
  }, [days, n, slot, stakeMax, innerH]);

  if (!n) return null;

  const handleMove = (event) => {
    if (!surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? days[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  return (
    <div className="bd-volume">
      <div
        ref={surfaceRef}
        className="bd-volume-surface"
        onPointerMove={handleMove}
        onPointerLeave={() => setHoverIndex(null)}
      >
        <svg
          viewBox={`0 0 ${VOL_W} ${VOL_H}`}
          preserveAspectRatio="none"
          className="bd-volume-svg"
          role="img"
          aria-label={ariaLabel}
        >
          <defs>
            <linearGradient id={`${gradId}-stake`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#46c479" stopOpacity="0.22" />
              <stop offset="100%" stopColor="#46c479" stopOpacity="0.01" />
            </linearGradient>
          </defs>

          {[0.25, 0.5, 0.75, 1].map((ratio) => (
            <line
              key={ratio}
              x1={0}
              x2={VOL_W}
              y1={VOL_PT + innerH * (1 - ratio)}
              y2={VOL_PT + innerH * (1 - ratio)}
              className="bd-volume-grid"
              vectorEffect="non-scaling-stroke"
            />
          ))}

          {days.map((d, i) => {
            if (d.count <= 0) return null;
            const y = yCount(d.count);
            return (
              <rect
                key={d.date || i}
                x={(i + 0.5) * slot - barW / 2}
                y={y}
                width={barW}
                height={Math.max(VOL_H - VOL_PB - y, 1.5)}
                rx={2.5}
                className="bd-volume-bar"
                opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.4}
              />
            );
          })}

          <path d={areaPath} fill={`url(#${gradId}-stake)`} />
          <path
            d={linePath}
            className="bd-volume-line"
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
          {points.map((p, i) =>
            p.stake > 0 ? (
              <circle
                key={days[i].date || i}
                cx={p.x}
                cy={p.y}
                r={3}
                className="bd-volume-dot"
                opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.4}
              />
            ) : null
          )}

          {hover ? (
            <line
              x1={hoverXFrac * VOL_W}
              x2={hoverXFrac * VOL_W}
              y1={VOL_PT}
              y2={VOL_H - VOL_PB}
              className="bd-volume-crosshair"
              vectorEffect="non-scaling-stroke"
            />
          ) : null}
        </svg>

        <div className="bd-volume-labels" aria-hidden>
          {days.map((d) => (
            <span key={d.date}>{d.label}</span>
          ))}
        </div>

        {hover ? (
          <div
            className={`bd-volume-tooltip tabular-nums${
              hoverXFrac > 0.68 ? " bd-volume-tooltip-left" : ""
            }`}
            style={{ left: `${hoverXFrac * 100}%` }}
          >
            <em>{hover.label}</em>
            <strong>
              {hover.count} ticket{hover.count === 1 ? "" : "s"}
            </strong>
            <span className="bd-tone-up">{compactNum(hover.sumStake)} staked</span>
          </div>
        ) : null}
      </div>

      <div className="bd-volume-legend" aria-hidden>
        <span>
          <i className="bd-volume-key-bar" /> Tickets placed
        </span>
        <span>
          <i className="bd-volume-key-line" /> Staked amount
        </span>
      </div>
    </div>
  );
};

/* ─── Odds profile: histogram of totalOdds ─── */

const ODDS_W = 320;
const ODDS_H = 118;
const ODDS_PT = 14;
const ODDS_PB = 20;

export const TicketOddsHistogram = ({ histogram, ariaLabel }) => {
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  if (!histogram?.loaded) return null;
  const { buckets, maxCount, sample } = histogram;
  const n = buckets.length;
  const innerH = ODDS_H - ODDS_PT - ODDS_PB;
  const slot = ODDS_W / n;
  const barW = Math.min(slot * 0.56, 40);

  const handleMove = (event) => {
    if (!surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? buckets[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  return (
    <div
      ref={surfaceRef}
      className="bd-odds-surface"
      onPointerMove={handleMove}
      onPointerLeave={() => setHoverIndex(null)}
    >
      <svg
        viewBox={`0 0 ${ODDS_W} ${ODDS_H}`}
        preserveAspectRatio="none"
        className="bd-odds-svg"
        role="img"
        aria-label={ariaLabel}
      >
        {[0.25, 0.5, 0.75, 1].map((ratio) => (
          <line
            key={ratio}
            x1={0}
            x2={ODDS_W}
            y1={ODDS_PT + innerH * (1 - ratio)}
            y2={ODDS_PT + innerH * (1 - ratio)}
            className="bd-hist-grid"
            vectorEffect="non-scaling-stroke"
          />
        ))}
        {buckets.map((bucket, i) => {
          const h = bucket.count > 0 ? Math.max((bucket.count / maxCount) * innerH, 2) : 0;
          if (h === 0) return null;
          return (
            <rect
              key={bucket.key}
              x={(i + 0.5) * slot - barW / 2}
              y={ODDS_PT + innerH - h}
              width={barW}
              height={h}
              rx={2.5}
              className="bd-odds-bar"
              opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.4}
            />
          );
        })}
        {hover ? (
          <line
            x1={hoverXFrac * ODDS_W}
            x2={hoverXFrac * ODDS_W}
            y1={ODDS_PT}
            y2={ODDS_H - ODDS_PB}
            className="bd-hist-crosshair"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      <div className="bd-hist-labels" aria-hidden>
        {buckets.map((bucket) => (
          <span key={bucket.key}>{bucket.label}</span>
        ))}
      </div>

      {hover ? (
        <div
          className={`bd-hist-tooltip tabular-nums${hoverXFrac > 0.68 ? " bd-hist-tooltip-left" : ""}`}
          style={{ left: `${hoverXFrac * 100}%` }}
        >
          <em>{hover.label} odds</em>
          <strong>
            {hover.count} ticket{hover.count === 1 ? "" : "s"}
          </strong>
          <span>{Math.round((hover.count / Math.max(sample, 1)) * 100)}% of loaded</span>
        </div>
      ) : null}
    </div>
  );
};

/* ─── Status flow, house view: lost = house keeps (green), won/paid = house pays (red) ─── */

export const TicketHouseFlow = ({ byStatus, ariaLabel }) => {
  const segments = HOUSE_FLOW_ORDER.map((key) => ({
    key,
    count: Number(byStatus?.[key]) || 0,
    meta: houseFlowMeta(key),
  })).filter((seg) => seg.count > 0);

  const total = segments.reduce((s, seg) => s + seg.count, 0);
  if (!total) return null;

  const keepRate = houseKeepRate(byStatus);

  return (
    <div className="bd-flow" role="img" aria-label={ariaLabel}>
      <div className="bd-flow-bar" aria-hidden>
        {segments.map((seg) => (
          <span
            key={seg.key}
            className="bd-flow-seg"
            style={{
              width: `${(seg.count / total) * 100}%`,
              backgroundColor: seg.meta.color,
            }}
            title={`${seg.meta.label}: ${seg.count} · ${seg.meta.houseLabel}`}
          />
        ))}
      </div>

      <ul className="bd-flow-legend">
        {segments.map((seg) => (
          <li key={seg.key}>
            <i style={{ backgroundColor: seg.meta.color }} aria-hidden />
            <span className="bd-flow-legend-label">{seg.meta.label}</span>
            <strong className="tabular-nums">{seg.count}</strong>
            <em>{seg.meta.houseLabel}</em>
          </li>
        ))}
      </ul>

      {keepRate != null ? (
        <p className="bd-flow-keep">
          House kept the stake on{" "}
          <strong className={`tabular-nums ${keepRate >= 50 ? "bd-tone-up" : "bd-tone-down"}`}>
            {keepRate}%
          </strong>{" "}
          of settled tickets.
        </p>
      ) : null}
    </div>
  );
};

/* ─── Liability concentration meter ─── */

export const TicketLiabilityMeter = ({ concentration, topTicket }) => {
  if (!concentration?.loaded) return null;
  const { sharePct, risky } = concentration;

  return (
    <div className={`bd-liab-meter ${risky ? "bd-liab-meter-risk" : ""}`}>
      <div className="bd-liab-meter-meta">
        <span>Biggest single ticket</span>
        <strong className="tabular-nums">{sharePct}% of exposure</strong>
      </div>
      <div className="bd-liab-meter-track" aria-hidden>
        <span className="bd-liab-meter-fill" style={{ width: `${Math.max(sharePct, 2)}%` }} />
      </div>
      <p className="bd-liab-meter-hint">
        {risky
          ? `Exposure is concentrated — ${
              topTicket?.username || "one bettor"
            } alone could take ${sharePct}% of your open liability.`
          : "Open liability is spread across tickets."}
      </p>
    </div>
  );
};

/* ─── Stake range (kept from the original strip) ─── */

export const TicketStakeRange = ({ minStake, avgStake, maxStake }) => {
  if (minStake == null || maxStake == null || maxStake <= 0) return null;
  const span = maxStake - minStake || 1;
  const avgPct =
    avgStake != null ? Math.min(100, Math.max(0, ((avgStake - minStake) / span) * 100)) : 50;

  return (
    <div className="bd-ticket-viz bd-ticket-stake-range">
      <div className="bd-ticket-stake-track" aria-hidden>
        <span className="bd-ticket-stake-fill" />
        {avgStake != null ? (
          <span className="bd-ticket-stake-avg" style={{ left: `${avgPct}%` }} />
        ) : null}
      </div>
      <div className="bd-ticket-stake-labels">
        <span>
          <em>Min</em>
          <strong className="tabular-nums">{Math.round(minStake).toLocaleString()}</strong>
        </span>
        {avgStake != null ? (
          <span>
            <em>Avg</em>
            <strong className="tabular-nums">{Math.round(avgStake).toLocaleString()}</strong>
          </span>
        ) : null}
        <span>
          <em>Max</em>
          <strong className="tabular-nums">{Math.round(maxStake).toLocaleString()}</strong>
        </span>
      </div>
    </div>
  );
};
